import { z } from 'zod'

/**
 * What a key may do on one bucket.
 *
 * Not a strict ladder: `rw` permits deletion, which neither `ro` nor `wo` does,
 * so it is more than the union of the two.
 */
export const S3AccessLevel = z.enum(['ro', 'wo', 'rw', 'full'])
export type S3AccessLevel = z.infer<typeof S3AccessLevel>

export const S3_ACCESS_LEVELS = S3AccessLevel.options

export const BucketRef = z.object({
  id: z.number(),
  name: z.string(),
  // Optional for one release. The frontend and backend roll independently into
  // the same namespace, so if this lands first a required field would fail the
  // whole parse and the credential list would silently stop updating.
  access: S3AccessLevel.default('full')
})
export type BucketRef = z.infer<typeof BucketRef>

export const S3Credential = z.object({
  id: z.number(),
  access_key_id: z.string(),
  name: z.string(),
  active: z.boolean(),
  created: z.string(),
  last_used: z.string().nullable().optional(),
  allowed_buckets: z.array(BucketRef).default([]),
  // Applied to every bucket in the account when the key is unscoped.
  default_access: S3AccessLevel.default('full'),
  // Whether the key is restricted to named buckets. Unscoped keys reach the
  // whole account at default_access.
  scoped: z.boolean().default(false)
})
export type S3Credential = z.infer<typeof S3Credential>

/** One bucket and the level a key holds on it. */
export const S3BucketScope = z.object({
  bucket_id: z.number(),
  access: S3AccessLevel
})
export type S3BucketScope = z.infer<typeof S3BucketScope>

export const S3CredentialCreateRequest = z.object({
  name: z.string().min(1),
  buckets: z.array(S3BucketScope).optional(),
  default_access: S3AccessLevel.optional()
})
export type S3CredentialCreateRequest = z.infer<typeof S3CredentialCreateRequest>

export const S3CredentialScopeRequest = z.object({
  buckets: z.array(S3BucketScope),
  default_access: S3AccessLevel
})
export type S3CredentialScopeRequest = z.infer<typeof S3CredentialScopeRequest>

export const S3CredentialCreateResponse = z.object({
  secret_key: z.string(),
  credential: S3Credential
})
export type S3CredentialCreateResponse = z.infer<typeof S3CredentialCreateResponse>
